/**
 * Spatial fusion across facial regions.
 *
 * A single region of interest is a bet. The forehead is usually the best
 * perfused, but it is also where hair, glasses and specular highlights live;
 * cheeks are steadier but weaker. Rather than pick one and hope, sample several
 * and combine them.
 *
 * The combination is done in the frequency domain, not the time domain, and the
 * distinction matters. Averaging the raw traces assumes the pulse arrives at
 * every region in phase — it does not, quite, and a phase mismatch cancels
 * signal. Averaging the power spectra is phase-insensitive, so regions can only
 * reinforce each other. It is the same variance-reduction trick as Welch
 * averaging, applied across space instead of time, and it costs no frequency
 * resolution.
 *
 * Regions are weighted by their own spectral prominence, so a cheek that has
 * drifted out of frame or caught a highlight contributes almost nothing rather
 * than dragging the estimate towards noise.
 */

import { bandPeak, powerSpectrum } from './dsp.js';

/**
 * Combine several power spectra into one, weighted.
 * All spectra must share a bin grid — same length and same `df`.
 */
export function fuseSpectra(spectra, weights) {
  const usable = spectra.filter(Boolean);
  if (!usable.length) return null;
  if (usable.length === 1) return usable[0];

  const size = usable[0].power.length;
  const w = weights && weights.length === usable.length ? weights.slice() : usable.map(() => 1);

  let total = 0;
  for (let i = 0; i < w.length; i++) {
    if (!Number.isFinite(w[i]) || w[i] < 0) w[i] = 0;
    total += w[i];
  }
  if (total === 0) {
    for (let i = 0; i < w.length; i++) w[i] = 1;
    total = w.length;
  }

  const acc = new Float64Array(size);
  for (let s = 0; s < usable.length; s++) {
    const p = usable[s].power;
    const scale = w[s] / total;
    if (scale === 0) continue;
    // Normalise each region by its own total power first, so a bright region
    // does not outvote a dim one purely on brightness.
    let sum = 0;
    for (let k = 0; k < size; k++) sum += p[k];
    const norm = sum > 0 ? 1 / sum : 0;
    for (let k = 0; k < size; k++) acc[k] += p[k] * norm * scale;
  }

  return { power: acc, df: usable[0].df, fs: usable[0].fs, size: usable[0].size, fused: usable.length };
}

/**
 * Analyse several regions and fuse them.
 *
 * @param {Array<Float64Array>} regions  one pulse signal per region
 * @param {object} opts  { fs, padFactor, band: [lo, hi], excludeHz }
 * @returns {{spectrum, perRegion: Array, weights: Array}|null}
 */
export function fuseRegions(regions, opts = {}) {
  const { fs = 30, padFactor = 4, band = [0.7, 4.0], excludeHz = 0.25 } = opts;
  const usable = regions.filter((r) => r && r.length >= 64);
  if (!usable.length) return null;

  const spectra = [];
  const perRegion = [];
  const weights = [];

  for (const signal of usable) {
    const spec = powerSpectrum(signal, { fs, padFactor });
    const peak = bandPeak(spec, band[0], band[1], { excludeHz });
    spectra.push(spec);
    perRegion.push(
      peak
        ? { bpm: peak.freq * 60, prominence: peak.prominence, snr: peak.snr }
        : { bpm: null, prominence: 0, snr: 0 },
    );
    // Weight by prominence, floored so a mediocre region still counts a little
    // and capped so one very clean region does not become the only vote.
    const p = peak && Number.isFinite(peak.prominence) ? peak.prominence : 0;
    weights.push(Math.min(10, Math.max(0.1, p)));
  }

  return { spectrum: fuseSpectra(spectra, weights), perRegion, weights };
}

/**
 * How much the regions agree with each other.
 *
 * Independent regions of the same face should report the same heart rate. When
 * they disagree, something is wrong that a single-region estimator would have
 * had no way of noticing — motion, a partial occlusion, a flickering light. It
 * is a genuine second opinion, and it is used as a lock criterion rather than
 * merely displayed.
 *
 * @returns {{agreement: number, spreadBpm: number, contributing: number}}
 */
export function regionAgreement(perRegion, toleranceBpm = 5) {
  const rates = perRegion.filter((r) => r.bpm !== null && r.prominence >= 1.5).map((r) => r.bpm);
  if (rates.length < 2) {
    return { agreement: rates.length === 1 ? 1 : 0, spreadBpm: 0, contributing: rates.length };
  }

  rates.sort((a, b) => a - b);
  const median = rates[Math.floor(rates.length / 2)];
  const agreeing = rates.filter((r) => Math.abs(r - median) <= toleranceBpm).length;

  return {
    agreement: agreeing / rates.length,
    spreadBpm: rates[rates.length - 1] - rates[0],
    contributing: rates.length,
  };
}

/**
 * Cross-method agreement.
 *
 * `regionAgreement` asks whether different parts of the face agree. This asks
 * something stronger: whether independent *extraction methods* agree.
 *
 * It matters because the two families fail differently. Green-only mistakes a
 * head movement for a pulse; POS does not. So when a subject moves, green
 * converges confidently on the motion frequency while POS converges on the
 * heartbeat — both internally consistent, both prominent, and completely
 * different. Region agreement cannot see this, because all three green regions
 * agree with each other perfectly. Only comparing across methods reveals it.
 *
 * This is the check that keeps a lock trustworthy under motion.
 *
 * @returns {{agreed: boolean, spreadBpm: number, methods: number, byMethod: object}}
 */
export function crossMethodAgreement(perRegion, toleranceBpm = 5, minProminence = 1.2) {
  const groups = new Map();
  for (const r of perRegion) {
    if (r.bpm === null || r.prominence < minProminence) continue;
    const key = r.method ?? 'default';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r.bpm);
  }

  const medians = [];
  const byMethod = {};
  for (const [method, rates] of groups) {
    rates.sort((a, b) => a - b);
    const median = rates[Math.floor(rates.length / 2)];
    medians.push(median);
    byMethod[method] = { bpm: Number(median.toFixed(1)), samples: rates.length };
  }

  // One method contributing means there is nothing to cross-check. That is not
  // agreement, but it is not disagreement either — the remaining criteria decide.
  if (medians.length < 2) {
    return { agreed: true, spreadBpm: 0, methods: medians.length, byMethod };
  }

  const spread = Math.max(...medians) - Math.min(...medians);
  return { agreed: spread <= toleranceBpm, spreadBpm: spread, methods: medians.length, byMethod };
}
